//@ts-nocheck

import {WalletContext} from '#/contexts/wallet-provider';
import environment from '#/environment/environment';
import {useLands} from '#/hooks/query/use-lands';
import {useLastProcessedApiBlockNumber} from '#/hooks/query/use-last-processed-api-block-number';
import {MarketEventLog, transactionAbiEvents, transactionUtils} from '#/utils/transaction-utils';
import {DefaultChildren} from '#/utils/utils';
import {DisplayableShareLandTransaction, isEqualLowerCase} from '@shareland/shareland-common';
import {FunctionComponent, createContext, useCallback, useContext, useEffect, useState} from 'react';

export interface UncachedTransactionsContextValue {
  getLandTransactions: (landId: number, blockNumber?: number) => DisplayableShareLandTransaction[];
  getUserTransactions: (account: string, blockNumber?: number) => DisplayableShareLandTransaction[];
  getTransactions: () => DisplayableShareLandTransaction[];
}

export const UncachedTransactionsContext = createContext<UncachedTransactionsContextValue>(
  {} as UncachedTransactionsContextValue,
);

export const UncachedTransactionsProvider: FunctionComponent<DefaultChildren> = ({children}) => {
  // Context
  const {accountResult, webSocketPublicClient} = useContext(WalletContext);
  const {address: account} = accountResult;

  // Hooks
  const {data: lastProcessedBlock} = useLastProcessedApiBlockNumber();
  const {data: lands = []} = useLands();

  // State
  const [uncachedTransactions, setUncachedTransactions] = useState<DisplayableShareLandTransaction[]>([]);
  const getLandTransactions = useCallback(
    (landId: number, blockNumber?: number) =>
      uncachedTransactions.filter(elem => elem.landId === landId && (!blockNumber || elem.blockNumber > blockNumber)),
    [uncachedTransactions],
  );
  const getUserTransactions = useCallback(
    (account: string, blockNumber?: number) =>
      uncachedTransactions.filter(
        elem => isEqualLowerCase(elem.account, account) && (!blockNumber || elem.blockNumber > blockNumber),
      ),
    [uncachedTransactions],
  );
  const getTransactions = useCallback(() => uncachedTransactions, [uncachedTransactions]);

  // Set transactions by remvoing duplicates and sorting by latest block number.
  const addTransactions = useCallback(
    (transactions: DisplayableShareLandTransaction[]) => {
      setUncachedTransactions(uncachedTransactions =>
        transactionUtils.sortByLatestBlockNumber(
          transactionUtils.removeDuplicates([...transactions, ...uncachedTransactions]),
        ),
      );
    },
    [setUncachedTransactions],
  );

  useEffect(() => {
    if (lastProcessedBlock) {
      setUncachedTransactions(uncachedTransactions =>
        uncachedTransactions.filter(elem => elem.blockNumber > lastProcessedBlock),
      );
    }
  }, [lastProcessedBlock, setUncachedTransactions]);

  useEffect(() => {
    // Return early if any of the required data is not avaiable yet.
    if (environment.isWaitlistNet || !account || !webSocketPublicClient || !lastProcessedBlock || lands.length === 0) {
      return;
    }

    // Fetch older event logs between the target block from API and the last processed block. Convert to transactions.
    const setOlderTransactions = async () => {
      const marketTransactionEventLogs = (
        await Promise.all(
          Object.values(transactionAbiEvents).map(event =>
            webSocketPublicClient.getLogs({
              event: {name: event.name, inputs: event.inputs, type: 'event'},
              fromBlock: BigInt(lastProcessedBlock + 1),
            }),
          ),
        )
      ).flat();

      const shareLandTransactions = marketTransactionEventLogs
        .map(eventLog => transactionUtils.eventLogToShareLandTransaction(eventLog, lands))
        .filter((elem): elem is DisplayableShareLandTransaction => !!elem);

      addTransactions(shareLandTransactions);
    };
    setOlderTransactions();

    // Set listener for new event logs. Convert to transactions.
    const providerLogCallback = (eventLogs: MarketEventLog[]) => {
      eventLogs.forEach(eventLog => {
        const transaction = transactionUtils.eventLogToShareLandTransaction(eventLog, lands);
        if (transaction) {
          addTransactions([transaction]);
        }
      });
    };
    const unwatches = Object.values(transactionAbiEvents).map(event =>
      webSocketPublicClient.watchEvent({
        event: {name: event.name, inputs: event.inputs, type: 'event'},
        onLogs: providerLogCallback,
      }),
    );

    // Unsubscribe from listeners on unmount.
    return () => {
      unwatches.forEach(unwatch => {
        unwatch();
      });
    };
  }, [account, addTransactions, lands, lastProcessedBlock, webSocketPublicClient]);

  return (
    <UncachedTransactionsContext.Provider value={{getLandTransactions, getUserTransactions, getTransactions}}>
      {children}
    </UncachedTransactionsContext.Provider>
  );
};
